import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Clock, FolderOpen, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { bizDate } from '@/lib/dates';
import { WrDetailDrawer } from './WrDetailDrawer';

// V5.1 §C — documentary expiration alerts panel (computed read-time, no dupes).
export const WrAlerts = ({ t, canEdit, fetchDetail, saveRecord, onChanged }) => {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [req, setReq] = useState('all');
  const [state, setState] = useState('all');
  const [cell, setCell] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/wr/alerts');
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const depts = useMemo(() => [...new Set(items.map((i) => i.department_name).filter(Boolean))].sort(), [items]);
  const reqs = useMemo(() => {
    const m = new Map();
    items.forEach((i) => m.set(i.requirement_id, i.requirement_name));
    return [...m.entries()];
  }, [items]);

  const rows = items.filter((i) => {
    if (search && !(i.employee_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (dept !== 'all' && i.department_name !== dept) return false;
    if (req !== 'all' && i.requirement_id !== req) return false;
    if (state !== 'all' && i.state !== state) return false;
    return true;
  });

  const openFile = (i) => setCell({
    employee: { id: i.employee_id, name: i.employee_name, department_name: i.department_name, position: i.position },
    requirement: { id: i.requirement_id, name: i.requirement_name, expires: true },
  });

  if (!data) return null;
  const c = data.counts || {};

  return (
    <div className="space-y-5" data-testid="wr-alerts-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant="outline" className="text-sm bg-red-50 text-red-700 border-red-200" data-testid="wr-alerts-expired-count">
          <AlertTriangle className="h-3.5 w-3.5 mr-1" />
          {c.expired ?? 0} {L('vencido(s)', 'expired')}
        </Badge>
        <Badge variant="outline" className="text-sm bg-orange-50 text-orange-700 border-orange-200" data-testid="wr-alerts-soon-count">
          <Clock className="h-3.5 w-3.5 mr-1" />
          {c.expiring_soon ?? 0} {L('por vencer', 'expiring soon')}
        </Badge>
        <span className="text-sm text-muted-foreground">
          {L(`Anticipación configurada: ${data.expiring_soon_days} días (ajustable en Configurar requisitos).`,
             `Configured anticipation: ${data.expiring_soon_days} days (adjustable in Configure requirements).`)}
        </span>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder={L('Buscar colaborador…', 'Search employee…')} data-testid="wr-alerts-filter-search" />
          </div>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="wr-alerts-filter-dept"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('Todos los departamentos', 'All departments')}</SelectItem>
              {depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={req} onValueChange={setReq}>
            <SelectTrigger data-testid="wr-alerts-filter-req"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('Todos los requisitos', 'All requirements')}</SelectItem>
              {reqs.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="wr-alerts-filter-state"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{L('Cualquier estado', 'Any state')}</SelectItem>
              <SelectItem value="expired">{L('Vencidos', 'Expired')}</SelectItem>
              <SelectItem value="expiring_soon">{L('Por vencer', 'Expiring soon')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="wr-alerts-table-card">
        {rows.length === 0 ? (
          <p className="p-10 text-base text-muted-foreground text-center" data-testid="wr-alerts-empty">
            {items.length === 0
              ? L('Sin alertas: no hay documentos vencidos ni próximos a vencer.',
                  'No alerts: no expired or soon-to-expire documents.')
              : L('Sin resultados con los filtros actuales.', 'No results with the current filters.')}
          </p>
        ) : (
          <div className="overflow-auto max-h-[64vh]">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left">
                  <th className="px-4 py-3 font-semibold">{L('Colaborador', 'Employee')}</th>
                  <th className="px-4 py-3 font-semibold">{L('Departamento', 'Department')}</th>
                  <th className="px-4 py-3 font-semibold">{L('Requisito', 'Requirement')}</th>
                  <th className="px-4 py-3 font-semibold">{L('Origen', 'Source')}</th>
                  <th className="px-4 py-3 font-semibold">{L('Vencimiento', 'Expiration')}</th>
                  <th className="px-4 py-3 font-semibold">{L('Estado', 'State')}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={`${i.employee_id}-${i.requirement_id}-${i.document_id || 'rec'}`} className="border-b hover:bg-muted/20"
                      data-testid={`wr-alert-row-${i.employee_id}-${i.requirement_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{i.employee_name}</p>
                      {i.position && <p className="text-xs text-muted-foreground">{i.position}</p>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{i.department_name || '—'}</td>
                    <td className="px-4 py-3">{i.requirement_name || '—'}</td>
                    <td className="px-4 py-3">
                      {i.source === 'document' ? (
                        <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700 border-sky-200"
                               title={i.document_name} data-testid={`wr-alert-source-doc-${i.document_id}`}>
                          {L('Documento', 'Document')}
                          <span className="ml-1 max-w-[9rem] truncate font-normal">{i.document_name}</span>
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">{L('Requisito', 'Requirement')}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {i.expiration_date ? bizDate(i.expiration_date, i18n.language) : '—'}
                      {i.days_left != null && (
                        <span className={`block text-xs ${i.days_left < 0 ? 'text-red-600' : 'text-orange-600'}`}>
                          {i.days_left < 0
                            ? L(`vencido hace ${Math.abs(i.days_left)} día(s)`, `expired ${Math.abs(i.days_left)} day(s) ago`)
                            : L(`vence en ${i.days_left} día(s)`, `expires in ${i.days_left} day(s)`)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline"
                             className={`text-xs ${i.state === 'expired'
                               ? 'bg-red-50 text-red-700 border-red-200'
                               : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                        {i.state === 'expired' ? L('Vencido', 'Expired') : L('Por vencer', 'Expiring soon')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {i.requirement_id && i.employee_id ? (
                        <Button size="sm" variant="outline" onClick={() => openFile(i)}
                                data-testid={`wr-alert-open-${i.employee_id}-${i.requirement_id}`}>
                          <FolderOpen className="h-3.5 w-3.5 mr-1" />
                          {L('Abrir expediente', 'Open file')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <WrDetailDrawer t={t} cell={cell} canEdit={canEdit} onClose={() => setCell(null)}
                      fetchDetail={fetchDetail} saveRecord={saveRecord}
                      onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
};
